/**
 * Utility function for asynchronous operations.
 *
 * Example usage:
 * ```typescript
 *   // Create a deferred object
 *   const isReady = createDeferred<void>();
 *
 *   // Wait for the promise to be resolved
 *   await isReady.promise;
 *
 *   // Meanwhile in another part of your code:
 *   // Resolve the promise when the async operation is done
 *   doAsyncStuff().then(() => { isReady.resolve() });
 * ```
 *
 * Can be cleaned up in the future when browsers support Promise.withResolvers()
 * https://caniuse.com/mdn-javascript_builtins_promise_withresolvers
 */
export function createDeferred<T = void>() {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: any) => void;

  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return { promise, resolve, reject };
}
